import { useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown, ArrowLeftRight, Coins } from 'lucide-react';
import { useCauldronTokenBySymbol } from '../hooks/useCauldronToken';
import { cauldronSwapUrl, formatBch, formatPricePerToken } from '../lib/cauldron';

// ── Inline token card ────────────────────────────────────────────
//
// Rendered when a note mentions a $TICKER that resolves to a real CashToken
// with a Cauldron pool. While resolving (or if the ticker is unknown), we fall
// back to a lightweight inline chip so unknown $WORDS never block a post.
//
// Read-only: all data comes from the Cauldron indexer. The "Trade" button is a
// top-level link-out to Cauldron — no wallet code runs next to the user's keys.

/** Lightweight inline chip used while loading and as the unknown-ticker fallback. */
function TickerChip({ symbol }: { symbol: string }) {
  return (
    <span className="font-semibold text-primary">${symbol}</span>
  );
}

export default function TokenCard({ symbol }: { symbol: string }) {
  const { token, loading } = useCauldronTokenBySymbol(symbol);

  // Unknown ticker (resolved to nothing) → plain chip, no card.
  if (!loading && !token) return <TickerChip symbol={symbol} />;

  // Still resolving → chip (card pops in once data arrives). Keeps the feed
  // from jumping around for every $word while lookups are in flight.
  if (loading && !token) return <TickerChip symbol={symbol} />;

  if (!token) return <TickerChip symbol={symbol} />;

  const up = token.change24h >= 0;

  return (
    <Link
      to={`/token/${token.tokenId}`}
      className="no-navigate glass-card !p-3 my-2 flex items-center gap-3 hover:border-primary/40 hover:shadow-md transition-all no-underline"
    >
      <TokenIcon iconUrl={token.iconUrl} symbol={token.symbol} />

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="font-bold text-sm truncate">${token.symbol}</span>
          {token.name && token.name.toLowerCase() !== token.symbol.toLowerCase() && (
            <span className="text-xs text-muted-foreground truncate">{token.name}</span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5 text-xs">
          <span className="font-semibold">{formatPricePerToken(token.priceUsd)}</span>
          {token.change24h !== 0 && (
            <span className={`inline-flex items-center gap-0.5 font-medium ${up ? 'text-emerald-500' : 'text-red-500'}`}>
              {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {up ? '+' : ''}{token.change24h.toFixed(2)}%
            </span>
          )}
          <span className="text-muted-foreground">· TVL {formatBch(token.tvlBch)}</span>
        </div>
      </div>

      <a
        href={cauldronSwapUrl(token.tokenId)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="no-navigate shrink-0 inline-flex items-center gap-1 rounded-lg bg-primary/10 text-primary px-2.5 py-1.5 text-xs font-semibold hover:bg-primary/20 transition-colors"
      >
        <ArrowLeftRight className="w-3.5 h-3.5" /> Trade
      </a>
    </Link>
  );
}

/** Token icon with a graceful fallback to a Coins glyph if the image fails. */
function TokenIcon({ iconUrl, symbol }: { iconUrl: string; symbol: string }) {
  const [failed, setFailed] = useState(false);
  if (failed || !iconUrl) {
    return (
      <div className="shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary" aria-label={symbol}>
        <Coins className="w-5 h-5" />
      </div>
    );
  }
  return (
    <img
      src={iconUrl}
      alt={symbol}
      width={40}
      height={40}
      loading="lazy"
      decoding="async"
      className="shrink-0 w-10 h-10 rounded-full bg-muted object-cover"
      onError={() => setFailed(true)}
    />
  );
}
